/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, func, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import useSalesforcePersonalizedContentQuery from '../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import GenericSkeleton from '../../../../GraphqlSkeletonComponents/GenericSkeleton';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import GiftFinderEds from './GiftFinderEds';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrandName } from '../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import GiftFinderFiveOhEds from './GiftFinderFiveOhEds';

const useStyle = makeStyles((theme) => ({
    fiveOhGiftFinder: {
        display: 'block',
        '& .giftFinderEdsInnerContainer': {
            backgroundColor: 'red',
        },
    },
}));

const GiftFinderFiveOhAbTestContainer = ({
    data, handleSubmitEds, userSubmittedZip, handleChange, onSubmitCheck, zip, validatedZip, setSelectedOccasion, buttonStyle,
}) => {
    const classes = useStyle();
    const brandName = useSelector(getBrandName);
    const location = useLocation();
    const dispatch = useDispatch();
    const fiveOhGiftFinderDesignEnabled = useSelector(getFeatureFlag('five-oh-giftfinder-design-enabled'));
    const forceFiveOhGiftFinderDesignEnabled = useSelector(getFeatureFlag('force-five-oh-giftfinder'));
    const pageUrls = useSelector(getFeatureFlag('is-jump-branded-page-ab-test'));
    let pageType = 'home page';
    if (location?.pathname?.includes(pageUrls?.jump)) {
        pageType = 'jump page';
    } else if (location?.pathname?.includes(pageUrls?.branded)) {
        pageType = 'branded page';
    }
    const interactionName = `${brandName} - mobile - ${pageType} - giftfinderupdate - v2`;
    const isMobile = useMediaQuery('(max-width: 680px)');
    const { data: fiveOhGiftFinderData, loading, variables } = useSalesforcePersonalizedContentQuery({
        queryName: 'giftFinderFiveOhTest',
        interactionName,
        featureFlag: fiveOhGiftFinderDesignEnabled && !forceFiveOhGiftFinderDesignEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    // Control or Test(variant) value from Decouple Gift Finder Sales Force Test
    const fiveOhGiftFinderUserGroup = fiveOhGiftFinderData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    useClickStreamCustomEventsTracking({
        salesforceResponse: fiveOhGiftFinderData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof fiveOhGiftFinderUserGroup !== 'undefined' && typeof window !== 'undefined' && !window.wuFiveOhGiftFinderEventFired && fiveOhGiftFinderUserGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'GiftFinderUpdate',
                eventLabel: fiveOhGiftFinderUserGroup?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: fiveOhGiftFinderUserGroup?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_element: 'GiftFinderUpdate',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuFiveOhGiftFinderEventFired = true;
        }
        return () => {
            window.wuFiveOhGiftFinderEventFired = false;
        };
    }, [fiveOhGiftFinderUserGroup]);

    if (variables?.skip || loading) {
        return (
            <GenericSkeleton height="112px" width="100%" />
        );
    }

    return (
            fiveOhGiftFinderUserGroup?.toLowerCase() === 'test' || (forceFiveOhGiftFinderDesignEnabled && isMobile)
                ? (
                    <GiftFinderFiveOhEds
                        data={data}
                        handleSubmitEds={handleSubmitEds}
                        userSubmittedZip={userSubmittedZip}
                        handleChange={handleChange}
                        onSubmitCheck={onSubmitCheck}
                        zip={zip}
                        validatedZip={validatedZip}
                        setSelectedOccasion={setSelectedOccasion}
                        buttonStyle={buttonStyle}
                    />
                )
                : (
                    <GiftFinderEds
                        data={data}
                        handleSubmitEds={handleSubmitEds}
                        userSubmittedZip={userSubmittedZip}
                        handleChange={handleChange}
                        onSubmitCheck={onSubmitCheck}
                        zip={zip}
                        validatedZip={validatedZip}
                        setSelectedOccasion={setSelectedOccasion}
                        buttonStyle={buttonStyle}
                    />
                )

    );
};

GiftFinderFiveOhAbTestContainer.propTypes = {
    buttonStyle: object,
    data: object.isRequired,
    onSubmitCheck: func.isRequired,
    handleChange: func.isRequired,
    handleSubmitEds: func.isRequired,
    validatedZip: string.isRequired,
    setSelectedOccasion: func.isRequired,
    userSubmittedZip: string.isRequired,
    zip: string,
};

GiftFinderFiveOhAbTestContainer.defaultProps = {
    zip: '',
    buttonStyle: {},
};

export default GiftFinderFiveOhAbTestContainer;
